import { codeFont } from '../../utils/fonts';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import LinkIcon from '@mui/icons-material/Link';
import EmailIcon from '@mui/icons-material/Email';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExtensionIcon from '@mui/icons-material/Extension';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import PlaceIcon from '@mui/icons-material/Place';
import { withTranslation } from 'react-i18next';
import { formatQuantity } from '../../utils';

const sourceIcons = {
  manual: <EditIcon fontSize="small" />,
  email: <EmailIcon fontSize="small" />,
  file_upload: <UploadFileIcon fontSize="small" />,
  url_parse: <LinkIcon fontSize="small" />,
  extension: <ExtensionIcon fontSize="small" />,
  catalogue: <DescriptionIcon fontSize="small" />,
};

const sourceColors = {
  manual: 'default',
  email: 'info',
  file_upload: 'success',
  url_parse: 'secondary',
  extension: 'warning',
  catalogue: 'info',
};

const sourceLabels = {
  manual: 'Manual Entry',
  email: 'Email Import',
  file_upload: 'File Upload',
  url_parse: 'URL Extract',
  extension: 'Extension Capture',
  catalogue: 'catalogues.sourceLabel',
};

// Document type icons and labels (Finnish)
const documentTypeConfig = {
  lasku: { icon: DescriptionIcon, color: 'primary', label: 'Lasku' },
  kuitti: { icon: ReceiptIcon, color: 'success', label: 'Kuitti' },
  lahete: { icon: LocalShippingIcon, color: 'info', label: 'Lähete' },
  hinnasto: { icon: DescriptionIcon, color: 'warning', label: 'Hinnasto' },
  tuoteluettelo: { icon: DescriptionIcon, color: 'secondary', label: 'Tuoteluettelo' },
  tarjous: { icon: DescriptionIcon, color: 'info', label: 'Tarjous' },
  kuvakaappaus: { icon: DescriptionIcon, color: 'default', label: 'Kuvakaappaus' },
  muu: { icon: DescriptionIcon, color: 'default', label: 'Muu' },
};

const PartDetailDialog = ({ t, open, onClose, part, onEdit, onPrintLabel }) => {
  const navigate = useNavigate();

  if (!part) return null;

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fi-FI', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fi-FI', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    });
  };

  const handleMaintenanceClick = (maintenanceId) => {
    onClose();
    navigate(`/maintenances/${maintenanceId}`);
  };

  const maintenanceUsages = part.maintenance_usages || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '20px', mb: 0.5 }}>
            {part.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              icon={sourceIcons[part.source_type]}
              label={t(sourceLabels[part.source_type] || 'Unknown')}
              size="small"
              color={sourceColors[part.source_type] || 'default'}
              variant="outlined"
            />
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Margin VAT Badge */}
        {part.is_margin_vat_scheme && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label={t('Margin VAT scheme')}
              color="info"
              size="small"
              sx={{ fontWeight: 500 }}
            />
            <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 1 }}>
              {t('VAT is calculated on profit margin, not on the full price')}
            </Typography>
          </Box>
        )}

        {/* Price Section */}
        <Box
          sx={{
            p: 2.5,
            bgcolor: 'background.default',
            borderRadius: '12px',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
            {/* Purchase Price */}
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography color="text.secondary" sx={{ fontSize: '13px', mb: 0.5 }}>
                {part.is_margin_vat_scheme ? t('Paid purchase price') : t('Purchase price')}
              </Typography>

              {/* VAT-exclusive price (primary) */}
              <Typography
                sx={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: 'primary.main',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {part.is_margin_vat_scheme
                  ? formatPrice(part.margin_vat_purchase_price)
                  : formatPrice(part.price)}
              </Typography>
              {part.is_margin_vat_scheme ? (
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  {t('No deductible VAT')}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  {t('excl. VAT')}
                </Typography>
              )}

              {/* VAT-inclusive price (secondary) - only for non-margin VAT parts */}
              {!part.is_margin_vat_scheme && part.price !== null && part.price !== undefined && (
                <>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'text.secondary',
                      mt: 0.5,
                    }}
                  >
                    {formatPrice(
                      part.price_entered_with_vat && part.stored_price_incl_vat
                        ? part.stored_price_incl_vat
                        : parseFloat(part.price) * (1 + (parseFloat(part.vat_rate) || 25.5) / 100)
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                    {t('incl. VAT')} ({part.vat_rate || 25.5}%)
                  </Typography>
                </>
              )}

              {/* Catalogue deposit (pantti) — displayed only, never billed automatically */}
              {parseFloat(part.catalogue_deposit) > 0 && (
                <Typography sx={{ fontSize: '12px', color: 'info.main', mt: 0.5 }} data-testid="part-deposit">
                  {t('catalogues.deposit')}: {formatPrice(part.catalogue_deposit)}
                </Typography>
              )}
            </Box>

            {/* Default Sell Price (only if set) */}
            {part.sell_price !== null && part.sell_price !== undefined && (
              <Box sx={{ textAlign: 'center', flex: 1, borderLeft: 1, borderColor: 'divider', pl: 3 }}>
                <Typography color="text.secondary" sx={{ fontSize: '13px', mb: 0.5 }}>
                  {part.is_margin_vat_scheme ? t('Customer price') : t('Default sell price')}
                </Typography>

                <Typography
                  sx={{
                    fontSize: '24px',
                    fontWeight: 700,
                    color: 'text.primary',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {formatPrice(part.sell_price)}
                </Typography>
                {part.is_margin_vat_scheme ? (
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    {t('No deductible VAT')}
                  </Typography>
                ) : (
                  <>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                      {t('excl. VAT')}
                    </Typography>

                    {/* VAT-inclusive sell price (secondary) — only for normal parts */}
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'text.secondary',
                        mt: 0.5,
                      }}
                    >
                      {formatPrice(
                        part.sell_price_entered_with_vat && part.stored_sell_price_incl_vat
                          ? part.stored_sell_price_incl_vat
                          : parseFloat(part.sell_price) * (1 + (parseFloat(part.vat_rate) || 25.5) / 100)
                      )}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                      {t('incl. VAT')} ({part.vat_rate || 25.5}%)
                    </Typography>

                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 1, fontStyle: 'italic' }}>
                      {t('Bypasses margin calculation')}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Description */}
        {part.description && (
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', mb: 1, textTransform: 'uppercase' }}
            >
              {t('Description')}
            </Typography>
            <Typography sx={{ color: 'text.primary' }}>{part.description}</Typography>
          </Box>
        )}

        {/* Manufacturer Info */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', mb: 1.5, textTransform: 'uppercase' }}
          >
            {t('Manufacturer Information')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
            <Box>
              <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
                {t('Manufacturer')}
              </Typography>
              <Typography sx={{ fontWeight: 500 }}>{part.manufacturer_name || '-'}</Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
                {t('Part Code')}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 500,
                  ...codeFont,
                  color: 'primary.main',
                }}
              >
                {part.primary_code || part.manufacturer_code || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
                {t('part_unit')}
              </Typography>
              <Typography sx={{ fontWeight: 500 }}>{part.unit || 'kpl'}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Identifier Codes */}
        {part.identifiers && part.identifiers.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', mb: 1.5, textTransform: 'uppercase' }}
            >
              {t('identifiers')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {part.identifiers.map((identifier) => (
                <Box
                  key={identifier.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    bgcolor: 'action.hover',
                    borderRadius: '8px',
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    sx={{
                      ...codeFont,
                      fontWeight: 500,
                      fontSize: '14px',
                    }}
                  >
                    {identifier.code}
                  </Typography>
                  <Chip
                    label={t(`identifier_type_${identifier.identifier_type}`)}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '11px' }}
                  />
                  {identifier.is_primary && (
                    <Chip
                      label={t('identifier_primary')}
                      size="small"
                      color="primary"
                      sx={{ height: 22, fontSize: '11px' }}
                    />
                  )}
                  {identifier.manufacturer_name && (
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                      {identifier.manufacturer_name}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Stock Information - only shown when inventory tracking is enabled */}
        {part.stock_info && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', mb: 1.5, textTransform: 'uppercase' }}
              >
                {t('inventory.stockInformation')}
              </Typography>

              {/* Stock warnings */}
              {part.stock_info.is_negative && (
                <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
                  {t('inventory.negativeStock')}
                </Alert>
              )}
              {part.stock_info.is_low_stock && !part.stock_info.is_negative && (
                <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                  {t('inventory.lowStock')}
                </Alert>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                    {t('inventory.quantityInStock')}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '16px',
                      color: part.stock_info.is_negative ? 'error.main' : part.stock_info.is_low_stock ? 'warning.main' : 'text.primary',
                    }}
                  >
                    {formatQuantity(part.stock_info.quantity_in_stock)}
                  </Typography>
                </Box>
                <Box>
                  <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                    {t('inventory.reorderPoint')}
                  </Typography>
                  <Typography sx={{ fontWeight: 500 }}>
                    {part.stock_info.reorder_point != null ? formatQuantity(part.stock_info.reorder_point) : '-'}
                  </Typography>
                </Box>
                {part.stock_info.quantity_reserved !== undefined && (
                  <>
                    <Box>
                      <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                        {t('inventory.quantityReserved')}
                      </Typography>
                      <Typography sx={{ fontWeight: 500 }}>
                        {formatQuantity(part.stock_info.quantity_reserved)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                        {t('inventory.quantityFree')}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: parseFloat(part.stock_info.quantity_free) < 0 ? 'error.main' : 'text.primary',
                        }}
                      >
                        {formatQuantity(part.stock_info.quantity_free)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              {part.stock_info.shelf_location && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <PlaceIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Box>
                    <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                      {t('inventory.shelfLocation')}
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {part.stock_info.shelf_location}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Source Information */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', mb: 1.5, textTransform: 'uppercase' }}
          >
            {t('Source Information')}
          </Typography>

          {part.source_reference && (
            <Box sx={{ mb: 2 }}>
              <Typography color="text.secondary" sx={{ fontSize: '13px', mb: 0.5 }}>
                {t('Source Reference')}
              </Typography>
              {isValidUrl(part.source_reference) ? (
                <Link
                  href={part.source_reference}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '14px',
                  }}
                >
                  <LinkIcon fontSize="small" />
                  {new URL(part.source_reference).hostname}
                </Link>
              ) : (
                <Typography sx={{ fontSize: '14px' }}>{part.source_reference}</Typography>
              )}
            </Box>
          )}

          {/* Import Document Source */}
          {part.source_inbound_event_id && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <DescriptionIcon fontSize="small" color="primary" />
                <Box>
                  <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                    {t('Import Source')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {part.source_document_type && documentTypeConfig[part.source_document_type] && (
                      <Chip
                        label={documentTypeConfig[part.source_document_type].label}
                        size="small"
                        color={documentTypeConfig[part.source_document_type].color}
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
              <Link
                href={`/imports`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '13px',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <VisibilityIcon fontSize="small" />
                {t('View Import')}
              </Link>
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                  {t('Created by')}
                </Typography>
                <Typography sx={{ fontSize: '14px' }}>
                  {part.created_by_email || t('System')}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                  {t('Created at')}
                </Typography>
                <Typography sx={{ fontSize: '14px' }}>{formatDate(part.created_at)}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Vendor Info */}
        {part.vendor_name && (
          <Box sx={{ mt: 2 }}>
            <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
              {t('Vendor')}
            </Typography>
            <Typography sx={{ fontWeight: 500 }}>{part.vendor_name}</Typography>
          </Box>
        )}

        {/* Maintenance Usages */}
        {maintenanceUsages.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography
                sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', mb: 1, textTransform: 'uppercase' }}
              >
                {t('Used in maintenances')} ({maintenanceUsages.length})
              </Typography>
              <List dense disablePadding sx={{ mx: -2 }}>
                {maintenanceUsages.map((usage) => (
                  <ListItem key={usage.maintenance_id} disablePadding>
                    <ListItemButton
                      onClick={() => handleMaintenanceClick(usage.maintenance_id)}
                      sx={{
                        py: 1,
                        px: 2,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <DirectionsCarIcon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                              {usage.vehicle_info || t('Unknown vehicle')}
                            </Typography>
                            {usage.registration_number && (
                              <Chip
                                label={usage.registration_number}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '11px' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                            {usage.work_number && (
                              <Typography variant="caption" color="text.secondary">
                                #{usage.work_number}
                              </Typography>
                            )}
                            {usage.customer_name && (
                              <Typography variant="caption" color="text.secondary">
                                {usage.customer_name}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {formatShortDate(usage.created_at)}
                            </Typography>
                            <Typography variant="caption" color="primary">
                              {formatQuantity(usage.quantity)} {t('pcs')}
                            </Typography>
                          </Box>
                        }
                      />
                      <OpenInNewIcon fontSize="small" color="action" sx={{ ml: 1 }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, flexWrap: 'wrap', rowGap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: '10px',
            px: 3,
          }}
        >
          {t('Close')}
        </Button>
        {onPrintLabel && (
          <Button
            variant="outlined"
            onClick={onPrintLabel}
            startIcon={<PrintIcon />}
            data-testid="part-detail-print-label"
            sx={{ borderRadius: '10px', px: 2, whiteSpace: 'nowrap' }}
          >
            {t('partLabels.printLabel')}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={onEdit}
          startIcon={<EditIcon />}
          sx={{
            borderRadius: '10px',
            px: 3,
            bgcolor: 'secondary.main',
            '&:hover': { bgcolor: 'secondary.dark' },
          }}
        >
          {t('Edit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withTranslation()(PartDetailDialog);
